import React from "react";
import { StatusBadge, StatusType, STATUS_COLORS } from "../../common/StatusBadge";
import { formatDate } from "../../../utils/date-format";
import {
  RobotsTxtStatus,
  ROBOTS_TXT_STATUS_LABELS,
} from "../../../constants/robots-txt-status";

/** Label shown while a robots.txt re-check is in flight (badge + button). */
const CHECKING_LABEL = "Sprawdzam…";

/** Tooltip on the "Sprawdź ponownie" button — exposes the rate limit to users. */
const CHECK_AGAIN_TITLE = "Sprawdź ponownie (limit 10 na minutę)";

/**
 * Minimal source shape required by the cell. Kept narrow on purpose so the
 * component does not depend on the full `InformationSource` interface in
 * `GrantsSources.tsx`.
 */
export interface RobotsTxtStatusCellSource {
  id: string;
  name: string;
  robotsTxtStatus: RobotsTxtStatus;
  robotsTxtLastCheckedAt: string | null;
}

interface InlineOverride {
  status: RobotsTxtStatus;
  lastCheckedAt: string | null;
}

interface RobotsTxtStatusCellProps {
  source: RobotsTxtStatusCellSource;
  pendingCheck: Set<string>;
  inlineOverrides: Map<string, InlineOverride>;
  onCheck: (sourceId: string) => void;
}

/**
 * Maps robots.txt status to StatusBadge status type and display label.
 * If the derived key is not a valid StatusType, falls back to "unknown".
 */
const getRobotsTxtBadgeProps = (
  robotsTxtStatus: RobotsTxtStatus | string | undefined,
): { status: StatusType; label: string } => {
  const s = robotsTxtStatus as RobotsTxtStatus | undefined;
  const key = `robots-${s}` as StatusType;
  return {
    status: s && key in STATUS_COLORS ? key : "unknown",
    label: ROBOTS_TXT_STATUS_LABELS[s as RobotsTxtStatus] ?? (s ? String(s) : "Nieznany"),
  };
};

/**
 * Cell displaying robots.txt status for a single source in the sources table.
 * Handles pending state, inline override after manual check, and exposes the
 * "Sprawdź ponownie" action.
 */
export const RobotsTxtStatusCell: React.FC<RobotsTxtStatusCellProps> = ({
  source,
  pendingCheck,
  inlineOverrides,
  onCheck,
}) => {
  const isPending = pendingCheck.has(source.id);
  const inlineOverride = inlineOverrides.get(source.id);
  const effectiveStatus =
    inlineOverride?.status ?? source.robotsTxtStatus ?? RobotsTxtStatus.NOT_CHECKED;
  const effectiveLastCheckedAt =
    inlineOverride?.lastCheckedAt ?? source.robotsTxtLastCheckedAt;

  return (
    <div className="flex flex-col items-center gap-1">
      {isPending ? (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 whitespace-nowrap"
          role="status"
          aria-label="Sprawdzanie robots.txt w toku"
        >
          {CHECKING_LABEL}
        </span>
      ) : (
        <StatusBadge
          {...getRobotsTxtBadgeProps(effectiveStatus)}
          className="whitespace-nowrap"
          title={
            effectiveLastCheckedAt
              ? `Sprawdzono: ${formatDate(effectiveLastCheckedAt)}`
              : undefined
          }
          data-testid={`robots-txt-badge-${source.id}`}
        />
      )}
      {effectiveStatus !== RobotsTxtStatus.NOT_APPLICABLE && (
        <button
          onClick={() => onCheck(source.id)}
          disabled={isPending}
          className={`text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 rounded ${
            isPending
              ? "text-neutral-400 cursor-wait"
              : "text-primary-600 hover:text-primary-800"
          }`}
          aria-label={`Sprawdź robots.txt dla ${source.name}`}
          title={CHECK_AGAIN_TITLE}
          data-testid={`check-robots-txt-btn-${source.id}`}
        >
          {isPending ? CHECKING_LABEL : "Sprawdź ponownie"}
        </button>
      )}
    </div>
  );
};
